import { useEffect, useState } from "react";
import { api, TASK_CLASS_LABELS } from "@/lib/api";
import { Trend } from "@/components/Scores";

const SCORE_LABELS = { effort: "Effort", rest: "Rest", sharpness: "Sharpness", readiness: "Readiness" };

function cellColor(v) {
  const mag = Math.min(Math.abs(v) / 9, 1);
  if (v > 0.3) return { background: `rgba(22,163,74,${0.12 + mag * 0.5})` };
  if (v < -0.3) return { background: `rgba(220,38,38,${0.12 + mag * 0.5})` };
  return {};
}

export default function TaskLibrary() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/task-library").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="p-8 text-sm text-muted-foreground">Loading model…</div>;

  return (
    <div>
      <header className="min-h-16 border-b border-border flex flex-wrap items-center py-3 px-8 gap-4 sticky top-0 bg-background z-10">
        <h1 className="font-heading text-xl font-black tracking-tight">Task Library</h1>
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Auto-derived impact scores · effort / rest / sharpness / readiness</span>
      </header>
      <div className="p-8">
        <p className="text-sm text-muted-foreground max-w-2xl mb-6">
          Impact scores are auto-derived (read-only) by the correlation model from the cleaned activity log · how much one unit of each task type moves each score per day. Green raises, red lowers. The simulator projects these forward.
        </p>

        <div className="border border-border overflow-x-auto mb-8">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">
                <th className="text-left p-3">Task Type</th>
                {data.scores.map((s) => <th key={s} className="text-right p-3">{SCORE_LABELS[s]}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.library.map((row) => (
                <tr key={row.task_class} className="border-b border-border last:border-0" data-testid={`lib-row-${row.task_class}`}>
                  <td className="p-3 font-medium">{TASK_CLASS_LABELS[row.task_class]}</td>
                  {data.scores.map((s) => {
                    const v = row.impact[s];
                    return <td key={s} className="p-3 text-right tabular-nums font-medium" style={cellColor(v)}>{v > 0 ? "+" : ""}{v}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-4">Activity templates & custom metrics</p>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-px bg-border border border-border">
          {data.library.map((row) => (
            <div key={row.task_class} className="bg-background p-4" data-testid={`lib-templates-${row.task_class}`}>
              <div className="font-heading font-bold tracking-tight mb-2">{TASK_CLASS_LABELS[row.task_class]}</div>
              <div className="space-y-2">
                {row.activities.map((act, i) => (
                  <div key={i} className="text-sm">
                    <span className="font-medium">{act.title}</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {act.metrics.map((m) => <span key={m} className="text-[11px] border border-border px-1.5 py-0.5 text-muted-foreground">{m}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
